export const INTRO_TEXT =
  "Welcome to our interactive map celebrating World Teachers’ Day in NSW! Dive into a heartwarming collection of messages from students across the state of New South Wales. Zoom in and click on a gem today to read all the tributes and memories shared. Celebrate your teaching gem by pinning a gem to our interactive map this World Teachers’ Day.";

/**
 * Normalises a build-time environment value.
 *
 * Bitbucket deployment variables are taken literally, so a value copied from
 * .env together with its quotes ('.' or 'pk.…') arrives with the quotes as
 * part of the value. That once shipped '.'/data/… URLs, a rejected Mapbox
 * token and a broken form link to staging at the same time — and never
 * reproduced locally, because dotenv strips those quotes.
 *
 * Trims whitespace and removes one pair of matching surrounding quotes
 * (' " or `). Returns undefined for an empty result so callers' fallbacks
 * still apply. Interior or mismatched quotes are left alone.
 *
 * Takes the value rather than the variable name: CRA only inlines REACT_APP_*
 * where the code spells out `process.env.REACT_APP_X` literally. A function
 * declaration, so it is hoisted above the constants that call it.
 */
export function normaliseEnv(value) {
  if (value === undefined || value === null) return undefined;
  let v = String(value).trim();
  const q = v[0];
  if (v.length >= 2 && (q === "'" || q === '"' || q === "`") && v[v.length - 1] === q) {
    v = v.slice(1, -1).trim();
  }
  return v === "" ? undefined : v;
}

/**
 * Base URL that data files and images are fetched from.
 *
 * CRA inlines REACT_APP_* at build time, so a build that runs without
 * REACT_APP_MEDIA_URL set bakes in the *string* "undefined" and every request
 * becomes "undefined/data/x.csv" — which is exactly how the staging deploy
 * broke. Falling back to "." degrades a missing build-time var to same-origin
 * instead of a guaranteed 403/404.
 */
export const MEDIA_BASE_URL = (
  normaliseEnv(process.env.REACT_APP_MEDIA_URL) || "."
).replace(/\/+$/, ""); // a trailing slash would otherwise yield "//data/…"

/** Mapbox GL public token; see normaliseEnv for why it is not read raw. */
export const MAPBOX_ACCESS_TOKEN = normaliseEnv(
  process.env.REACT_APP_MAPBOX_ACCESS_TOKEN
);

/**
 * Target of the "Message" call-to-action.
 *
 * Deliberately a constant rather than REACT_APP_FORM_URL. A deployment
 * variable that is set always beats a code default, and Bitbucket's copy still
 * held the 2025 form, so staging and production kept linking to last year's
 * form however this file changed. The form is replaced once a year along with
 * the rest of the copy here, so it belongs here.
 *
 * REACT_APP_FORM_URL is no longer read and can be deleted from the Staging and
 * Production deployment variables.
 */
export const FORM_URL =
  "https://forms.cloud.microsoft/Pages/ResponsePage.aspx?id=8qN-znY9mkW3M-Ve7HMrC-6h_PybLvZBtArW99lRMSBUMkQ4TVoyTlpRNDk0N1laSTBITUhYQkgzUS4u";

// export const MESSAGES_DATA_URL = MEDIA_URL+"/data/cr225_shoutoutses.csv";
// export const MESSAGES_DATA_URL = process.env.REACT_APP_MEDIA_URL+"/data/NESA World Teachers Day 2023 Shout-Out Form 1.csv";
export const MESSAGES_DATA_URL =
  MEDIA_BASE_URL + "/data/moderated_submissions.csv";

// export const FIELD_PREFIX = "cr225_";
export const FIELD_PREFIX = "";

export const PRESET_SHOUTOUTS = [
  "for your deep knowledge",
  "for your sense of humour",
  "for your support",
];

// export const GEOLOCATION_DATA_URL = MEDIA_URL+"/data/cr225_tables.csv";
// export const BBOX_DATA_URL = MEDIA_URL+"/data/nsw_sal_bboxes.geojson";

/** Per-school coordinates, so gems sit on the school rather than the postcode's centroid. */
export const SCHOOL_LOCATIONS_URL =
  MEDIA_BASE_URL + "/data/school_locations.csv";

/** NSW state outline, used to keep gems on NSW land rather than in the ocean or another state. */
export const NSW_BOUNDARY_URL = MEDIA_BASE_URL + "/data/nsw_boundary.geojson";
export const GEOLOCATION_DATA_URL =
  MEDIA_BASE_URL + "/data/nsw_postcode_centroids.csv";
export const BBOX_DATA_URL =
  MEDIA_BASE_URL + "/data/nsw_postcode_bboxes.geojson";

export const UTF16 = false;

// Two shout-outs that always appear on the map regardless of what's in the
// submissions CSV. Update this list (not DataLoaderHelper.js) for next
// year's minister/CEO messages.
export const SPECIAL_FEATURES = [
  {
    // "special-ceo" maps to the yellow gem in SCHOOL_LEVEL_ICON_NAMES (Map.js).
    id: "special-ceo",
    // Drummoyne Boys' High School has long since closed, so this sits on the
    // suburb rather than a school that no longer exists.
    coordinates: [151.1545, -33.8511],
    message:
      "Shoutout to my Ancient History teacher, Mr Bierling from Drummoyne Boys’ High School, who instilled a love of Thucydides in me!",
    school: "Paul Martin, CEO of the NSW Education Standards Authority (NESA)",
    date: "01/09/2026 09:00",
  },
  {
    // "special-minister" falls through to DEFAULT_ICON_NAME, the purple gem.
    id: "special-minister",
    // Caroline Chisholm College, Glenmore Park.
    coordinates: [150.66954, -33.788525],
    message:
      "I wouldn’t be where I am today without the support of my English teacher, Mrs Elborough from Caroline Chisholm College. She encouraged my love of English and my interest in current affairs. She changed my life forever.",
    school:
      "The Hon. Prue Car MP, NSW Deputy Premier and Minister for Education and Early Learning",
    date: "01/09/2026 09:00",
  },
];
